import type {
  PuppyParsedTopologyRuntimeAsset,
  PuppyParsedTopologyRuntimeDocument,
} from './types'
import { PUPPY_PACKAGE_VERSION } from './types'

/**
 * 判断未知值是否为普通对象。
 *
 * @param value 未知值。
 * @returns 是否为记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object')
}

/**
 * 读取必填非空字符串。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 字段值。
 */
function readRequiredString(record: Record<string, unknown>, key: string): string {
  const value = record[key]
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`PuppyCAD topology-runtime 字段缺失或无效: ${key}`)
  }
  return value.trim()
}

/**
 * 读取可选非空字符串。
 *
 * @param record 记录对象。
 * @param key 字段名。
 * @returns 字段值。
 */
function readOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 读取三角形范围。
 *
 * @param value 未知值。
 * @returns 三角形范围数组。
 */
function readFaceTriangleRanges(value: unknown): [number, number][] | undefined {
  if (value === undefined) {
    return undefined
  }
  if (!Array.isArray(value)) {
    throw new Error('PuppyCAD topology-runtime faceTriangleRanges 必须是数组')
  }

  return value.map((item) => {
    if (
      !Array.isArray(item)
      || item.length !== 2
      || !Number.isInteger(item[0])
      || !Number.isInteger(item[1])
      || item[0] < 0
      || item[1] < item[0]
    ) {
      throw new Error('PuppyCAD topology-runtime faceTriangleRanges 包含无效范围')
    }
    return [item[0], item[1]] as [number, number]
  })
}

/**
 * 从 topologySnapshot.faces 中兜底读取 faceTriangleRanges。
 *
 * @param snapshot 拓扑快照。
 * @returns faceTriangleRanges。
 */
function createFaceTriangleRangesFromSnapshot(snapshot: TopologySnapshot): [number, number][] {
  return snapshot.faces
    .map((face) => face.triangleRange)
    .filter((range): range is [number, number] => (
      Array.isArray(range)
      && range.length === 2
      && Number.isInteger(range[0])
      && Number.isInteger(range[1])
      && range[0] >= 0
      && range[1] >= range[0]
    ))
}

/**
 * 读取拓扑快照。
 *
 * @param value 未知值。
 * @returns 拓扑快照。
 */
function readTopologySnapshot(value: unknown): TopologySnapshot {
  if (!isRecord(value) || !Array.isArray(value.vertices) || !Array.isArray(value.edges) || !Array.isArray(value.faces)) {
    throw new Error('PuppyCAD topology-runtime 缺少有效 topologySnapshot')
  }

  return {
    vertices: value.vertices as TopologySnapshotVertex[],
    edges: value.edges as TopologySnapshotEdge[],
    faces: value.faces as TopologySnapshotFace[],
  }
}

/**
 * 克隆拓扑资产元数据。
 *
 * @param value 未知元数据。
 * @returns 规范化后的元数据。
 */
function cloneMetadata(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? { ...value } : undefined
}

/**
 * 解析单个拓扑资产。
 *
 * @param value 未知值。
 * @returns 解析后的拓扑资产。
 */
function parseTopologyRuntimeAsset(value: unknown): PuppyParsedTopologyRuntimeAsset {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD topology-runtime asset 必须是对象')
  }

  const bodyBindingId = readRequiredString(value, 'bodyBindingId')
  const geometryObjectId = readRequiredString(value, 'geometryObjectId')
  const topologySnapshot = readTopologySnapshot(value.topologySnapshot)

  return {
    bodyBindingId,
    geometryObjectId,
    exportDefinitionId: readOptionalString(value, 'exportDefinitionId'),
    bodyId: readOptionalString(value, 'bodyId'),
    faceTriangleRanges: readFaceTriangleRanges(value.faceTriangleRanges) ?? createFaceTriangleRangesFromSnapshot(topologySnapshot),
    metadata: {
      ...(cloneMetadata(value.metadata) ?? {}),
      objectId: geometryObjectId,
      bodyBindingId,
      bodyId: readOptionalString(value, 'bodyId'),
      source: 'puppy-topology-runtime',
    },
    topologySnapshot,
  }
}

/**
 * 解析 `.puppy` 内部交互拓扑快照文档。
 *
 * @param value 未知 JSON。
 * @returns 解析后的拓扑 runtime 文档。
 * @throws 当输入不是对象、版本不受支持或缺少非空 `assets` 数组时。
 */
export function parsePuppyTopologyRuntimeDocument(value: unknown): PuppyParsedTopologyRuntimeDocument {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD topology-runtime 必须是对象')
  }

  if (value.version !== PUPPY_PACKAGE_VERSION) {
    throw new Error(`不支持的 PuppyCAD topology-runtime 版本: ${String(value.version)}`)
  }

  if (!Array.isArray(value.assets) || value.assets.length === 0) {
    throw new Error('PuppyCAD topology-runtime 缺少 assets')
  }

  return {
    version: PUPPY_PACKAGE_VERSION,
    assets: value.assets.map(parseTopologyRuntimeAsset),
  }
}
